import React, { useState } from "react";
import styled from "styled-components";

const TableContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const TableTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ToggleButton = styled.button`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const ExportButton = styled.button`
  background-color: #6ed216;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: #6ed216;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  display: ${(props) => (props.isOpen ? "table" : "none")};
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }

  &:hover {
    background-color: #f0f0f0;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  font-weight: 500;
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
`;

// Ajout d'un style pour la ligne de totaux
const TotalRow = styled(TableRow)`
  font-weight: bold;
  background-color: #f5f5f5;

  &:hover {
    background-color: #f0f0f0;
  }
`;

function DataTable({ data, selectedFilter }) {
  const [isOpen, setIsOpen] = useState(false);

  // Mise à jour de la fonction getFilteredData pour gérer correctement les données
  const getFilteredData = () => {
    if (selectedFilter === "day") {
      // Regrouper par jour
      const dailyData = {};
      data.forEach((item) => {
        if (!dailyData[item.date]) {
          dailyData[item.date] = {
            date: item.date,
            impressions: 0,
            clicks: 0,
            budget: 0,
            viewableImpressions: 0,
            viewability: 0,
            totalWeight: 0,
          };
        }

        // Accumuler les valeurs de base
        dailyData[item.date].impressions += Number(item.impressions || 0);
        dailyData[item.date].clicks += Number(item.clicks || 0);
        dailyData[item.date].budget += Number(item.budget || 0);
        
        // Accumuler les impressions visibles
        dailyData[item.date].viewableImpressions += Number(item.viewableImpressions || 0);
      });

      // Finaliser les calculs
      Object.values(dailyData).forEach((day) => {
        // Calculer la viewability directement à partir des impressions visibles
        day.viewability = day.impressions > 0 
          ? (day.viewableImpressions / day.impressions) * 100 
          : 0;

        // Calculer les autres métriques
        day.ctr = day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0;
        day.cpm = day.impressions > 0 ? (day.budget / day.impressions) * 1000 : 0;
        day.cpc = day.clicks > 0 ? day.budget / day.clicks : 0;
      });

      return Object.values(dailyData).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
    } else if (selectedFilter === "creative") {
      // Regrouper par bannière
      const creativeData = {};
      data.forEach((item) => {
        const creativeKey =
          item.creative ||
          item.creativeName ||
          `Creative ${item.creativeId || "Unknown"}`;

        if (!creativeData[creativeKey]) {
          creativeData[creativeKey] = {
            creative: creativeKey,
            impressions: 0,
            clicks: 0,
            budget: 0,
            viewableImpressions: 0,
          };
        }

        // Accumuler les valeurs de base
        creativeData[creativeKey].impressions += Number(item.impressions || 0);
        creativeData[creativeKey].clicks += Number(item.clicks || 0);
        creativeData[creativeKey].budget += Number(item.budget || 0);
        creativeData[creativeKey].viewableImpressions += Number(item.viewableImpressions || 0);
      });

      // Finaliser les calculs
      Object.values(creativeData).forEach((creative) => {
        // Calculer la viewability directement à partir des impressions visibles
        creative.viewability = creative.impressions > 0 
          ? (creative.viewableImpressions / creative.impressions) * 100 
          : 0;

        // Calculer les autres métriques
        creative.ctr = creative.impressions > 0
          ? (creative.clicks / creative.impressions) * 100
          : 0;
        creative.cpm = creative.impressions > 0
          ? (creative.budget / creative.impressions) * 1000
          : 0;
        creative.cpc =
          creative.clicks > 0 ? creative.budget / creative.clicks : 0;
      });

      return Object.values(creativeData);
    } else if (selectedFilter === "size") {
      // Regrouper par taille
      const sizeData = {};
      data.forEach((item) => {
        if (!item.size) return;

        if (!sizeData[item.size]) {
          sizeData[item.size] = {
            size: item.size,
            impressions: 0,
            clicks: 0,
            budget: 0,
            viewableImpressions: 0,
          };
        }

        // Accumuler les valeurs de base
        sizeData[item.size].impressions += Number(item.impressions || 0);
        sizeData[item.size].clicks += Number(item.clicks || 0);
        sizeData[item.size].budget += Number(item.budget || 0);
        sizeData[item.size].viewableImpressions += Number(item.viewableImpressions || 0);
      });

      // Finaliser les calculs
      Object.values(sizeData).forEach((size) => {
        // Calculer la viewability directement à partir des impressions visibles
        size.viewability = size.impressions > 0 
          ? (size.viewableImpressions / size.impressions) * 100 
          : 0;

        // Calculer les autres métriques
        size.ctr =
          size.impressions > 0 ? (size.clicks / size.impressions) * 100 : 0;
        size.cpm =
          size.impressions > 0 ? (size.budget / size.impressions) * 1000 : 0;
        size.cpc = size.clicks > 0 ? size.budget / size.clicks : 0;
      });

      return Object.values(sizeData);
    }
    return [];
  };
  const tableData = getFilteredData();

  // Calculer les totaux
  const calculateTotals = () => {
    const totals = {
      impressions: 0,
      clicks: 0,
      budget: 0,
      viewableImpressions: 0,
    };

    tableData.forEach((item) => {
      totals.impressions += Number(item.impressions || 0);
      totals.clicks += Number(item.clicks || 0);
      totals.budget += Number(item.budget || 0);
      totals.viewableImpressions += Number(item.viewableImpressions || 0);
    });

    // Calculer la viewability directement à partir des impressions visibles
    totals.viewability = totals.impressions > 0 
      ? (totals.viewableImpressions / totals.impressions) * 100 
      : 0;

    // Calculer les autres métriques
    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
    totals.cpm = totals.impressions > 0 ? (totals.budget / totals.impressions) * 1000 : 0;
    totals.cpc = totals.clicks > 0 ? totals.budget / totals.clicks : 0;

    return totals;
  };

  const truncateText = (text, maxLength = 25) => {
    if (text && text.length > maxLength) {
      return `${text.substring(0, maxLength)}...`;
    }
    return text;
  };

  const totals = calculateTotals();

  // Déterminer la clé d'identification en fonction du filtre
  const getIdKey = () => {
    if (selectedFilter === "day") return "date";
    if (selectedFilter === "creative") return "creative";
    return "size";
  };

  const idKey = getIdKey();

  // Formater les nombres pour l'affichage
  const formatNumber = (num, decimals = 0) => {
    // Vérifier si la valeur est undefined ou null
    if (num === undefined || num === null) {
      return "0";
    }
    
    // S'assurer que num est un nombre
    const value = Number(num);
    
    // Vérifier si la conversion a réussi
    if (isNaN(value)) {
      return "0";
    }
    
    return value.toLocaleString("fr-FR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatPercentage = (num) => {
    if (num === undefined || num === null) {
      return "0,00%";
    }
    
    const value = Number(num);
    
    if (isNaN(value)) {
      return "0,00%";
    }
    
    return value.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + "%";
  };

  const formatCurrency = (num) => {
    if (num === undefined || num === null) {
      return "0,00 €";
    }
    
    const value = Number(num);
    
    if (isNaN(value)) {
      return "0,00 €";
    }
    
    return value.toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Fonction pour exporter les données en CSV
  const exportToCSV = () => {
    try {
      // Créer les en-têtes du CSV
      let csvContent = "data:text/csv;charset=utf-8,";

      // Déterminer le nom de la première colonne en fonction du filtre
      const firstColumnName =
        selectedFilter === "day"
          ? "Date"
          : selectedFilter === "creative"
          ? "Bannière"
          : "Taille";

      // Ajouter l'en-tête des colonnes
      const headers = [
        firstColumnName,
        "Impressions",
        "Impressions visibles",
        "Clics",
        "CTR (%)",
        "Visibilité (%)",
        "Budget (€)",
        "CPM (€)",
        "CPC (€)",
      ];
      csvContent += headers.join(";") + "\r\n";

      // Ajouter les données
      tableData.forEach((item) => {
        const row = [
          item[idKey],
          item.impressions,
          Math.round(item.impressions * item.viewability),
          item.clicks,
          item.ctr
            .toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            .replace(",", "."),
          (item.viewability * 100)
            .toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            .replace(",", "."),
          item.budget
            .toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            .replace(",", "."),
          item.cpm
            .toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
            .replace(",", "."),
          item.clicks > 0
            ? item.cpc
                .toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                .replace(",", ".")
            : "-",
        ];
        csvContent += row.join(";") + "\r\n";
      });

      // Ajouter la ligne de totaux
      const totalsRow = [
        "TOTAL",
        totals.impressions,
        Math.round(totals.viewableImpressions),
        totals.clicks,
        totals.ctr
          .toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          .replace(",", "."),
        (totals.viewability * 100)
          .toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          .replace(",", "."),
        totals.budget
          .toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          .replace(",", "."),
        totals.cpm
          .toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          .replace(",", "."),
        totals.clicks > 0
          ? totals.cpc
              .toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              .replace(",", ".")
          : "-",
      ];
      csvContent += totalsRow.join(";") + "\r\n";

      // Créer un lien de téléchargement
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `tableau_${selectedFilter}_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);

      // Déclencher le téléchargement
      link.click();

      // Nettoyer
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erreur lors de l'exportation en CSV:", error);
      alert("Une erreur est survenue lors de l'exportation en CSV.");
    }
  };

  return (
    <TableContainer>
      <TableHeader>
        <TableTitle>
          Données détaillées{" "}
          {selectedFilter === "day"
            ? "par jour"
            : selectedFilter === "creative"
            ? "par bannière"
            : "par taille"}
        </TableTitle>
        <ButtonsContainer>
          <ExportButton onClick={exportToCSV}>
            <span>📊</span> Exporter CSV
          </ExportButton>
          <ToggleButton onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? "Masquer le tableau" : "Afficher le tableau"}
            <span>{isOpen ? "▲" : "▼"}</span>
          </ToggleButton>
        </ButtonsContainer>
      </TableHeader>

      <StyledTable isOpen={isOpen}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>
              {selectedFilter === "day"
                ? "Date"
                : selectedFilter === "creative"
                ? "Bannière"
                : "Taille"}
            </TableHeaderCell>
            <TableHeaderCell>Impressions</TableHeaderCell>
            <TableHeaderCell>Imp. visibles</TableHeaderCell>
            <TableHeaderCell>Clics</TableHeaderCell>
            <TableHeaderCell>CTR %</TableHeaderCell>
            <TableHeaderCell>Visibilité %</TableHeaderCell>
            <TableHeaderCell>Budget</TableHeaderCell>
            <TableHeaderCell>CPM</TableHeaderCell>
            <TableHeaderCell>CPC</TableHeaderCell>
          </TableRow>
        </TableHead>
        <tbody>
          {tableData.map((item) => (
            <TableRow key={item[idKey]}>
              <TableCell
                data-tooltip={item[idKey]}
                title={item[idKey]} // Attribut title standard pour le tooltip natif
              >
                {truncateText(item[idKey])}
              </TableCell>
              <TableCell>{formatNumber(item.impressions)}</TableCell>
              <TableCell>{formatNumber(item.viewableImpressions)}</TableCell>
              <TableCell>{formatNumber(item.clicks)}</TableCell>
              <TableCell>{formatPercentage(item.ctr / 100)}</TableCell>
              <TableCell>{formatPercentage(item.viewability)}</TableCell>
              <TableCell>{formatCurrency(item.budget)}</TableCell>
              <TableCell>{formatCurrency(item.cpm)}</TableCell>
              <TableCell>
                {item.clicks > 0 ? formatCurrency(item.cpc) : "-"}
              </TableCell>
            </TableRow>
          ))}

          {/* Ligne de totaux */}
          <TotalRow>
            <TableCell>
              <strong>TOTAL</strong>
            </TableCell>
            <TableCell>{formatNumber(totals.impressions)}</TableCell>
            <TableCell>
              {formatNumber(Math.round(totals.viewableImpressions))}
            </TableCell>
            <TableCell>{formatNumber(totals.clicks)}</TableCell>
            <TableCell>{formatPercentage(totals.ctr / 100)}</TableCell>
            <TableCell>{formatPercentage(totals.viewability)}</TableCell>
            <TableCell>{formatCurrency(totals.budget)}</TableCell>
            <TableCell>{formatCurrency(totals.cpm)}</TableCell>
            <TableCell>
              {totals.clicks > 0 ? formatCurrency(totals.cpc) : "-"}
            </TableCell>
          </TotalRow>
        </tbody>
      </StyledTable>
    </TableContainer>
  );
}

export default DataTable;
